// SNIGJ - Audit Logs Page

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { auditAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileBarChart, 
  Search, 
  Filter,
  Clock,
  User,
  Activity,
  Eye,
  Edit,
  Plus,
  Trash,
  LogIn,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const AuditLogsPage = () => {
  const { language, user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: ''
  });

  const hasAccess = ['SUPER_ADMIN', 'NATIONAL_ADMIN', 'TECH_SUPPORT', 'FINANCIAL_AUDITOR', 'MINISTER', 'PRESIDENT'].includes(user?.role);

  useEffect(() => {
    if (hasAccess) {
      loadLogs();
    }
  }, [hasAccess, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = { ...filters, limit: 200 };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      const response = await auditAPI.list(params);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to load logs:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'VIEW': return Eye;
      case 'CREATE': return Plus;
      case 'UPDATE': return Edit;
      case 'DELETE': return Trash;
      case 'LOGIN': return LogIn;
      case 'LOGOUT': return LogIn;
      case 'REGISTER': return User;
      default: return Activity;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'VIEW': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30';
      case 'CREATE': return 'text-green-600 bg-green-100 dark:bg-green-900/30';
      case 'UPDATE': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30';
      case 'DELETE': return 'text-red-600 bg-red-100 dark:bg-red-900/30';
      case 'LOGIN': return 'text-purple-600 bg-purple-100 dark:bg-purple-900/30';
      case 'LOGOUT': return 'text-gray-600 bg-gray-100 dark:bg-gray-800';
      default: return 'text-slate-600 bg-slate-100 dark:bg-slate-800';
    }
  };

  const actions = ['VIEW', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'REGISTER', 'UPLOAD'];
  const resourceTypes = ['USER', 'CASE', 'PRISONER', 'DOCUMENT', 'PAYMENT', 'BORDER_ALERT'];

  if (!hasAccess) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <Shield className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="font-heading text-xl font-bold mb-2">
                {language === 'fr' ? 'Accès Restreint' : 'Access Restricted'}
              </h2>
              <p className="text-muted-foreground">
                {language === 'fr' 
                  ? "Vous n'avez pas les droits d'accès aux journaux d'audit."
                  : "You don't have access to audit logs."}
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-logs-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              {t(language, 'audit.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Historique des actions système' : 'System action history'}
            </p>
          </div>
          <Button variant="outline" onClick={loadLogs} className="gap-2">
            <Activity className="w-4 h-4" />
            {t(language, 'common.refresh')}
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t(language, 'common.search') + '...'}
                    className="pl-10"
                    data-testid="audit-search"
                  />
                </div>
              </div>
              <div className="w-[150px]">
                <Select
                  value={filters.action || "ALL"}
                  onValueChange={(value) => setFilters({ ...filters, action: value === "ALL" ? "" : value })}
                >
                  <SelectTrigger data-testid="filter-audit-action">
                    <SelectValue placeholder={t(language, 'audit.action')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Toutes' : 'All'}</SelectItem>
                    {actions.map((action) => (
                      <SelectItem key={action} value={action}>{action}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[180px]">
                <Select
                  value={filters.resource_type || "ALL"}
                  onValueChange={(value) => setFilters({ ...filters, resource_type: value === "ALL" ? "" : value })}
                >
                  <SelectTrigger data-testid="filter-audit-resource">
                    <SelectValue placeholder={t(language, 'audit.resourceType')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                    {resourceTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={loadLogs} className="gap-2">
                <Filter className="w-4 h-4" />
                {t(language, 'common.filter')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table table-dense">
                <thead>
                  <tr className="bg-muted/50">
                    <th>{t(language, 'audit.timestamp')}</th>
                    <th>{language === 'fr' ? 'Utilisateur' : 'User'}</th>
                    <th>{t(language, 'audit.action')}</th>
                    <th>{t(language, 'audit.resourceType')}</th>
                    <th>{t(language, 'audit.resourceId')}</th>
                    <th>{t(language, 'audit.ipAddress')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 10 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 6 }).map((_, j) => (
                          <td key={j}><div className="h-4 skeleton" /></td>
                        ))}
                      </tr>
                    ))
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-muted-foreground">
                        {t(language, 'common.noData')}
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => {
                      const ActionIcon = getActionIcon(log.action);
                      return (
                        <tr key={log.log_id} data-testid={`log-row-${log.log_id}`}>
                          <td>
                            <span className="flex items-center gap-1 text-xs font-mono">
                              <Clock className="w-3 h-3" />
                              {new Date(log.timestamp).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })}
                            </span>
                          </td>
                          <td>
                            <span className="text-xs">{log.user_email}</span>
                          </td>
                          <td>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getActionColor(log.action)}`}>
                              <ActionIcon className="w-3 h-3" />
                              {log.action}
                            </span>
                          </td>
                          <td>
                            <span className="text-xs font-medium">{log.resource_type}</span>
                          </td>
                          <td>
                            <span className="font-mono text-xs">{log.resource_id || '-'}</span>
                          </td>
                          <td>
                            <span className="font-mono text-xs text-muted-foreground">
                              {log.ip_address || '-'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="text-sm text-muted-foreground text-center">
          {language === 'fr' 
            ? `${logs.length} entrées affichées`
            : `${logs.length} entries displayed`}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AuditLogsPage;
